import { __rest } from 'tslib';
import { OverlayType, UnitEnum } from '@teko-builder/types/pb';
export { D as DeviceTypes, g as generate, a as isMobile, i as isMobileOnly, b as textAnchorCss, t as themeColor } from './text-21866551.js';
export { g as getRgbaFromHex } from './util-80df897f.js';
import 'ua-parser-js';
import 'color-name';
import '@emotion/react';

const isNumeric = (str) => {
    if (typeof str === 'number')
        return true;
    if (typeof str !== 'string')
        return false;
    return !isNaN(str) && !isNaN(parseFloat(str));
};
const jsToCss = (str) => str.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
const getCommonCss = (attribute) => {
    return (attribute &&
        Object.entries(attribute).reduce((acc, curr) => {
            const [key, value] = curr;
            if (!value || /Unit$/.test(key))
                return acc;
            return `${acc}\n${jsToCss(key)}: ${unitParse(value, attribute[`${key}Unit`])};`;
        }, ''));
};
const getFourSideCss = (props) => {
    const sidesOrder = ['top', 'right', 'bottom', 'left'];
    const corderOrder = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
    return Object.entries(props).reduce((acc, curr) => {
        const [key, value = {}] = curr;
        const { unit = UnitEnum.pixel } = value, restValue = __rest(value, ["unit"]);
        return (acc +
            '\n' +
            Object.keys(restValue)
                .filter((attr) => (sidesOrder.includes(attr) || corderOrder.includes(attr)) &&
                value[attr] !== null &&
                value[attr] !== undefined)
                .map((attr) => {
                let childValue;
                if (isNumeric(value[attr])) {
                    childValue = value[attr] + unit;
                }
                else {
                    childValue = value[attr];
                }
                return `${jsToCss(`${key}-${attr}`)}: ${childValue};`;
            })
                .join('\n'));
    }, '');
};
const getBackgroundOverlay = (type, value) => {
    if (type === OverlayType.color && value) {
        return `
      & {
        position: relative;

        > :not([class*="lb_resize"]) {
          position: relative;
        }
  
        &:before {
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          content: '';
          background-color: ${value};
          border-radius: inherit;
          z-index: 0;
        }
      }
    `;
    }
    return '';
};
const getBoxShadow = (shadow) => {
    let boxShadow = '';
    if (shadow === null || shadow === void 0 ? void 0 : shadow.color) {
        boxShadow = 'box-shadow: ';
        boxShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.x) ? `${shadow.x}px ` : '0 ';
        boxShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.y) ? `${shadow.y}px ` : '0 ';
        boxShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.blur) ? `${shadow.blur}px ` : '0 ';
        boxShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.spread) ? `${shadow.spread}px ` : '0 ';
        boxShadow += `${shadow.color};`;
    }
    return boxShadow;
};
const getTextShadow = (shadow) => {
    let textShadow = '';
    if (shadow === null || shadow === void 0 ? void 0 : shadow.color) {
        textShadow = 'text-shadow: ';
        textShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.x) ? `${shadow.x}px ` : '0 ';
        textShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.y) ? `${shadow.y}px ` : '0 ';
        textShadow += (shadow === null || shadow === void 0 ? void 0 : shadow.blur) ? `${shadow.blur}px ` : '0 ';
        textShadow += `${shadow.color};`;
    }
    return textShadow;
};
const getHoverStyle = (hover) => {
    return `
    &:hover {
      ${getCommonCss(Object.assign(Object.assign(Object.assign({}, ((hover === null || hover === void 0 ? void 0 : hover.color) && { color: hover.color })), ((hover === null || hover === void 0 ? void 0 : hover.backgroundColor) && {
        backgroundColor: hover.backgroundColor,
    })), ((hover === null || hover === void 0 ? void 0 : hover.borderColor) && { borderColor: hover.borderColor })))}
      ${(hover === null || hover === void 0 ? void 0 : hover.transparent) ? `opacity: ${hover.transparent / 100};` : ''}
      ${(hover === null || hover === void 0 ? void 0 : hover.zoom) ? `transform: scale(${hover.zoom / 100})` : ''}
    }
    `;
};
const getAnimation = (animation) => {
    return `
    ${(animation === null || animation === void 0 ? void 0 : animation.name) ? `animation-name: ${animation.name};` : ''}
    ${isNumeric(animation === null || animation === void 0 ? void 0 : animation.delay) ? `animation-delay: ${animation.delay}s;` : ''}
    ${isNumeric(animation === null || animation === void 0 ? void 0 : animation.duration)
        ? `animation-duration: ${animation.duration}s;`
        : ''}
    ${(animation === null || animation === void 0 ? void 0 : animation.repeatType) === 'infinite'
        ? `animation-iteration-count: infinite;`
        : isNumeric(animation === null || animation === void 0 ? void 0 : animation.repeatNumber)
            ? `animation-iteration-count: ${animation.repeatNumber};`
            : ''}
    `;
};
const getBackgroundImageUrl = (bgImage, bgImageWidth) => {
    const isUrlPrefix = /^url\((.*)\)$/.test(bgImage);
    if (!isUrlPrefix && bgImage) {
        return `url(${bgImage}${bgImageWidth ? `=w${bgImageWidth}` : ''})`;
    }
    return bgImage;
};
const getStyle = (props) => {
    if (!props)
        return '';
    const { margin = {}, padding = {}, border = {}, shadow, textShadow, backgroundOverlay, backgroundOverlayType, bgImageWidth, backgroundImage, hover = {}, animation = {} } = props, rest = __rest(props, ["margin", "padding", "border", "shadow", "textShadow", "backgroundOverlay", "backgroundOverlayType", "bgImageWidth", "backgroundImage", "hover", "animation"]);
    return `
    & {
      ${getBoxShadow(shadow)}
      ${getTextShadow(textShadow)}
      ${getCommonCss(Object.assign(Object.assign(Object.assign({}, border), rest), { backgroundImage: getBackgroundImageUrl(backgroundImage, bgImageWidth) }))}
      ${getFourSideCss({ margin, padding })}
      ${getBackgroundOverlay(backgroundOverlayType, backgroundOverlay)}
      ${getHoverStyle(hover)}
      ${getAnimation(animation)}
    }
    `;
};
const getScrollbarWidth = () => {
    let width = 0;
    if (typeof window !== 'undefined') {
        if (document.body.scrollHeight <= window.innerHeight)
            return 0;
        if (window.scrollbarWidth &&
            document.body.scrollHeight > window.innerHeight) {
            return window.scrollbarWidth;
        }
        const outer = document.createElement('div');
        outer.style.visibility = 'hidden';
        outer.style.overflow = 'scroll';
        document.body.appendChild(outer);
        const inner = document.createElement('div');
        outer.appendChild(inner);
        width = outer.offsetWidth - inner.offsetWidth;
        outer.parentNode.removeChild(outer);
    }
    return width;
};
const pixelParse = (value) => {
    if (typeof value === 'number')
        return `${value}px`;
    return value;
};
const unitParse = (value, unit = UnitEnum.pixel) => {
    if (typeof value === 'number')
        return `${value}${unit}`;
    return value;
};

export { getAnimation, getBackgroundOverlay, getBoxShadow, getCommonCss, getScrollbarWidth, getStyle, pixelParse, unitParse };
